import type { ISvdCard, ISvdCardVariant, ISvdDisplayConfig, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { mapSvdProduct } from './svd-card.mapper'
import { SVD_SWATCH_DISPLAY_TYPE } from './svd-product-card.helpers'

/**
 * Adapter: SVD-enabled **Presentation-Layer** card → native `SvdNativeProduct`.
 *
 * The SVD-shaped PL response (CLP `layouts/fetch` + client `widgets/fetch`) is structurally the
 * v4-BFF `ISvdCard` EXCEPT three wire-casing divergences the classic v4 page-data does not have —
 * normalized here before delegating to the shared `mapSvdProduct`:
 *   - variant shoppable id is `spId` (camel), not `spid`
 *   - product category is `productCategory` (camel), not `product_category`
 *   - `displayConfig.displayType` uses proper-case values ('Color' / 'Image' / 'Text') rather than the
 *     'HEX_COLOR' / 'IMAGE' / 'TEXT' the swatch renderer switches on
 *
 * TEMPORARY (STRFRNT-15810 · CLP SVD preview): delete alongside every `isPlSvdCard`/`mapPlSvdCard`
 * call site once every PL environment emits the canonical `ISvdCard` shape unconditionally and CLP
 * flows through the normal v4 path.
 */
type PlSvdCardVariant = Omit<ISvdCardVariant, 'spid'> & {
  spid?: number | null
  spId?: number | null
}

export type PlSvdCard = Omit<ISvdCard, 'cardVariants' | 'product_category'> & {
  cardVariants: PlSvdCardVariant[]
  product_category?: string | null
  productCategory?: string | null
}

// PL `displayType` (any case: 'Color' / 'HEX_COLOR' / ...) → the swatch renderer's enum, keyed
// lower-case so the lookup is case-insensitive. Unknown values pass through unchanged so a new
// backend value degrades to the default (hex) chip rather than mis-rendering.
const PL_DISPLAY_TYPE_TO_SWATCH: Record<string, string> = {
  color: SVD_SWATCH_DISPLAY_TYPE.HEX_COLOR,
  hex_color: SVD_SWATCH_DISPLAY_TYPE.HEX_COLOR,
  image: SVD_SWATCH_DISPLAY_TYPE.IMAGE,
  text: SVD_SWATCH_DISPLAY_TYPE.TEXT,
}

function normalizePlDisplayConfig(config: ISvdDisplayConfig | null): ISvdDisplayConfig | null {
  // Some PL widget types (e.g. horizontal product list, and Home's nested single-product-card
  // widgets) send a `displayConfig` with no `displayType` — sometimes `{}` rather than omitted
  // entirely. The swatch chip JSX switches on `displayType` for hex/image/text; without it there is
  // nothing to render. Normalize to null (not the useless object) so `shouldRenderSvdSwatches` /
  // `getSvdSwatchVariants` correctly treat the variant as non-swatchable instead of counting a
  // phantom swatch that renders as a blank, invisible chip.
  if (!config?.displayType) return null
  const mapped = PL_DISPLAY_TYPE_TO_SWATCH[config.displayType.toLowerCase()]
  return mapped ? { ...config, displayType: mapped } : config
}

/**
 * True when a card carries the SVD wire shape. Only the SVD-enabled PL backend emits an
 * `anchorAttributes` key on its variants at all — populated for a VDA product, `null` for a no-VDA
 * one (`README.md` §"A no-VDA product ... has `anchorAttributes: null`"). Checked by **key presence**,
 * not truthiness: a value check would treat a no-VDA SVD variant identically to a legacy/prod-PL
 * variant (which has no such key), sending it down the legacy branch instead of `mapPlSvdCard`. This
 * doubles as the "is this the swapped SVD response?" render guard on the shared CLP grid.
 */
export function isPlSvdCard(product: unknown): product is PlSvdCard {
  const variants = (product as { cardVariants?: unknown } | null | undefined)?.cardVariants
  if (!Array.isArray(variants)) return false
  return variants.some(
    (variant: unknown) => variant !== null && typeof variant === 'object' && 'anchorAttributes' in variant,
  )
}

/** Normalize a PL SVD card into the native `SvdNativeProduct` the SVD card renders. */
export function mapPlSvdCard(plCard: PlSvdCard): SvdNativeProduct {
  const normalized = {
    ...plCard,
    product_category: plCard.product_category ?? plCard.productCategory ?? null,
    cardVariants: plCard.cardVariants.map(variant => ({
      ...variant,
      spid: variant.spid ?? variant.spId ?? null,
      displayConfig: normalizePlDisplayConfig(variant.displayConfig),
    })),
  } as unknown as ISvdCard
  return mapSvdProduct(normalized)
}
